import React, { useEffect, useState } from 'react'

import classNames from 'classnames'
import Cookies from 'js-cookie'
import styled, { keyframes } from 'styled-components'

import { Icon } from '_components/icon'
import { palette } from '_styles/variables'

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(100%);
  },
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeOut = keyframes`  
  0% {
    opacity: 1;
    transform: translateY(0);
  },
  100% {
    opacity: 0;
    transform: translateY(100%);
  }
`

const Container = styled.div<{ zIndex?: number }>`
  position: fixed;
  z-index: ${({ zIndex }) => zIndex ?? 100000};
  font-size: 0.75rem;
  letter-spacing: 0;
  font-weight: 400;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${palette.brand.purpleDark};
  color: ${palette.neutral.white};
  padding: 1rem 2rem;
  box-sizing: border-box;
  text-align: center;
  opacity: 0;
  transform: translateY(100%);

  &.is-open {
    animation: ${fadeIn} 1s ease;
    opacity: 1;
    transform: translateY(0);
  }

  &.is-dismissed {
    animation: ${fadeOut} 1s ease;
  }

  .banner--content {
    position: relative;

    ${Icon} {
      position: absolute;
      font-size: 1.25rem;
      top: 50%;
      margin-top: -0.75rem;
      right: -1rem;

      &:hover,
      &:focus {
        color: ${palette.neutral.white};
      }
    }

    .banner--dismiss {
      margin-left: 0.5rem;
    }

    p {
      margin: 0;
      padding: 0;
      margin-bottom: 0.5rem;
      display: inline-block;
    }

    a,
    .banner--dismiss {
      display: inline-block;
      color: ${palette.brand.pink};
      text-decoration: none;
      padding: 0;
      border: none;
      background-color: transparent;
      cursor: pointer;

      &:hover,
      &:focus {
        color: ${palette.brand.teal};
        text-decoration: underline;
        background-color: transparent;
      }
    }
  }
`

export interface PrivacyBannerProps {
  privacyPolicyUrl: string
  onRequestClose(): void
  isOpen?: boolean
  zIndex?: number
}

export const PrivacyBanner: React.FC<PrivacyBannerProps> = ({ privacyPolicyUrl, isOpen, onRequestClose, zIndex }) => {
  const [isDismissed, setDismissed] = useState(false)

  useEffect(() => {
    const hasAccepted = Cookies.get('cookie-notice-accepted')

    if (hasAccepted === 'true') {
      onRequestClose()
    }
  }, [onRequestClose])

  const closeBanner = () => {
    onRequestClose()
    setDismissed(true)
  }

  const onAccept = () => {
    Cookies.set('cookie-notice-accepted', 'true', { expires: 7, path: '' })
    closeBanner()
  }

  return (
  <Container zIndex={zIndex} className={classNames({
    'is-open': isOpen,
    'is-dismissed': isDismissed
  })} >
    <div className="banner--content">
      <p>We use cookies to ensure that you have the best experience on our site. If you continue to browser the site, you are agreeing to our use of cookies. Review our <a href={privacyPolicyUrl} type="_blank">privacy policy</a> for more detail. </p>
      <button className='banner--dismiss' type="button" onClick={onAccept}>OK</button>
      <Icon icon="x" onClick={closeBanner} />
    </div>
  </Container>
)}
